import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import LazySection from './LazySection';
import { FooterSkeleton } from './SkeletonLoader';
import { AUDIENCES, setAudience } from '../utils/audience';
import './Footer.css';

// A quiet link out to the other side of the site. Following it also records the
// new audience choice, otherwise the splitter at "/" would send the visitor
// straight back to the side they just left.
const AUDIENCE_SWITCH = {
  attorney: {
    to: AUDIENCES.consumer.path,
    next: 'consumer',
    // No icon: a "?" glyph in front of "Have a legal question?" read as two
    // question marks. The consumer side keeps its scales, which say something
    // the label does not.
    label: 'Have a legal question?',
    cta: 'Switch to the Consumer Experience',
  },
  consumer: {
    to: AUDIENCES.attorney.path,
    next: 'attorney',
    icon: '\u2696',
    label: 'Are you an attorney or paralegal?',
    cta: 'Switch to the Professional Experience',
  },
};

const Footer = ({
  variant = 'default',
  className = '',
  style = {},
  disableLazy = false,
  // 'attorney' keeps About/FAQ in the link row; 'consumer' drops them. The
  // splitter itself opts out of the switch link — that page is the switch.
  audience = 'attorney',
  showAudienceSwitch = true,
}) => {
  const footerRef = useRef(null);
  const audienceSwitch = AUDIENCE_SWITCH[audience];

  // Simple footer variant (for LandingPage)
  if (variant === 'simple') {
    return (
      <motion.footer
        className={`footer footer-simple ${className}`}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3 }}
      >
        <div className="footer-content">
          <p>© {new Date().getFullYear()} LawFusion.ai — Multiple AIs. Stronger Arguments.</p>
          <p style={{ marginTop: '0.25rem' }}>
            <a href="mailto:support@lawfusion.ai" style={{ color: 'inherit', textDecoration: 'underline' }}>support@lawfusion.ai</a>
          </p>
        </div>
      </motion.footer>
    );
  }

  // Links only variant (for Login/Signup)
  if (variant === 'links-only') {
    return (
      <motion.div
        className={`footer-links-only ${className}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <Link to="/privacy-policy" className="auth-footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        <span className="auth-footer-separator">·</span>
        <Link to="/terms-of-service" className="auth-footer-link" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
        <span className="auth-footer-separator">·</span>
        <Link to="/faq" className="auth-footer-link" target="_blank" rel="noopener noreferrer">FAQ</Link>
        <span className="auth-footer-separator">·</span>
        <a href="mailto:support@lawfusion.ai" className="auth-footer-link">Support</a>
      </motion.div>
    );
  }

  // Compact one-row variant for the Research and Debate workspaces. Those
  // pages are fixed-height panels whose body owns the scroll, so the tall
  // gradient footer has nowhere to sit; this keeps the same identity, links
  // and audience switch in a single strip under the workspace.
  if (variant === 'compact') {
    return (
      <footer className={`footer footer-compact ${className}`} style={style}>
        <div className="footer-compact-inner">
          <span className="footer-compact-brand">
            <span aria-hidden="true">⚖️</span> LawFusion.AI
            <span className="footer-compact-tagline">
              {' '}· Multiple AIs. Stronger Arguments.
            </span>
          </span>
          <span className="footer-compact-copy">
            © {new Date().getFullYear()} LawFusion.AI · All Rights Reserved
          </span>
          <nav className="footer-compact-links" aria-label="Footer">
            {audience === 'attorney' && (
              <>
                <Link to="/about" className="footer-link" target="_blank" rel="noopener noreferrer">About</Link>
                <span className="footer-separator">·</span>
                <Link to="/faq" className="footer-link" target="_blank" rel="noopener noreferrer">FAQ</Link>
                <span className="footer-separator">·</span>
              </>
            )}
            <Link to="/privacy-policy" className="footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            <span className="footer-separator">·</span>
            <Link to="/terms-of-service" className="footer-link" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
            <span className="footer-separator">·</span>
            <a href="mailto:support@lawfusion.ai" className="footer-link">Support</a>
          </nav>
          {showAudienceSwitch && audienceSwitch && (
            <Link
              to={audienceSwitch.to}
              className="switch-pill switch-pill--compact"
              onClick={() => setAudience(audienceSwitch.next)}
            >
              <span className="switch-pill-label">{audienceSwitch.label}</span>
              <span className="switch-pill-cta">{audienceSwitch.cta}</span>
              <span className="switch-pill-arrow" aria-hidden="true">&rarr;</span>
            </Link>
          )}
        </div>
      </footer>
    );
  }

  // Default full footer (for Dashboard, DebateMode, About)
  const fullFooter = (
    <motion.footer
      ref={footerRef}
      className={`footer dashboard-footer ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      style={style}
    >
      <div className="footer-content">
        <motion.h2
          className="footer-title"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          ⚖️ LawFusion.AI
        </motion.h2>
        <motion.p
          className="footer-tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          Multiple AIs. Stronger Arguments.
        </motion.p>
        <div className="footer-copyright">
          © {new Date().getFullYear()} LawFusion.AI · All Rights Reserved
        </div>
        <p className="footer-description">
          Built for Legal Professionals · Empowered by Insight
        </p>
        <div className="footer-links">
          {audience === 'attorney' && (
            <>
              <Link
                to="/about"
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                About
              </Link>
              <span className="footer-separator">·</span>
              <Link
                to="/faq"
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </Link>
              <span className="footer-separator">·</span>
            </>
          )}
          <Link
            to="/privacy-policy"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </Link>
          <span className="footer-separator">·</span>
          <Link
            to="/terms-of-service"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </Link>
          <span className="footer-separator">·</span>
          <a
            href="mailto:support@lawfusion.ai"
            className="footer-link"
          >
            Support
          </a>
        </div>
        {showAudienceSwitch && audienceSwitch && (
          <div className="footer-audience-switch">
            <Link
              to={audienceSwitch.to}
              className="switch-pill"
              onClick={() => setAudience(audienceSwitch.next)}
            >
              {audienceSwitch.icon && (
                <span className="switch-pill-icon" aria-hidden="true">
                  {audienceSwitch.icon}
                </span>
              )}
              <span className="switch-pill-label">{audienceSwitch.label}</span>
              <span className="switch-pill-cta">{audienceSwitch.cta}</span>
              <span className="switch-pill-arrow" aria-hidden="true">
                &rarr;
              </span>
            </Link>
          </div>
        )}
      </div>
    </motion.footer>
  );

  if (disableLazy) {
    return fullFooter;
  }

  return (
    <LazySection
      options={{ threshold: 0.1, rootMargin: '300px' }}
      fallback={<FooterSkeleton />}
    >
      {fullFooter}
    </LazySection>
  );
};

export default Footer;

